import { Env } from '/@/Env';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { User } from '@/modules/user/models/User';
import SegmentHelper from '@/integrations/segment/SegmentHelper';
import { DefaultProperties } from '@/integrations/segment/types/DefaultProperties';
import { IdentifyTraitsProperties } from '@/integrations/segment/types/IdentifyTraitsProperties';
import { TrackProperties } from '@/integrations/segment/types/TrackProperties';
import { UserProperties } from '@/integrations/segment/types/UserProperties';
import { PageProperties } from '@/integrations/segment/types/PageProperties';
import Locale from '@/modules/user/Enum/Locale';

class SegmentService {
  user: AuthUser | User | null;

  groupId: string = '';

  private segmentHelper: typeof SegmentHelper;

  constructor() {
    this.user = null;
    this.segmentHelper = SegmentHelper;
  }

  setUser(user: AuthUser | User | null): void {
    this.user = user;
    this.groupId = this.user && this.user instanceof AuthUser ? this.user.currentEstate?.id : '';
  }

  environmentShouldTrack(): boolean {
    return (
      !window?.location.host.includes('localhost') &&
      !Env.isRunningUnitTest() &&
      !Env.isLocal() &&
      !Env.isRunningCypress()
    );
  }

  track(eventName: string, eventProps: Record<string, unknown>): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    const mergedProperties: Record<string, unknown> = {
      ...this.getTrackProperties(),
      ...eventProps
    };

    this.segmentHelper.track(eventName, mergedProperties);
  }

  page(
    name: string,
    path: string,
    referrer: string,
    properties: Record<string, unknown> = {},
    options: Record<string, unknown> = {}
  ): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    properties = { name, ...properties };
    const mergedProperties: Record<string, unknown> = {
      ...this.getPageProperties(path, referrer),
      ...properties
    };

    this.segmentHelper.page(name, mergedProperties, options);
  }

  init(user: AuthUser | null, includeAlias: boolean = false): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    this.segmentHelper.init();

    if (!user) {
      return;
    }

    this.setUser(user);
    if (includeAlias) {
      this.alias(this.user?.id);
    }
    this.identify();
  }

  alias(userId: number): void {
    this.segmentHelper.alias(userId);
  }

  identify(): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    const traits: IdentifyTraitsProperties = this.getIdentifyTraits();

    this.segmentHelper.identify(this.user?.id, traits);
  }

  identifyWithEventProperties(eventIdentifyProps: Record<string, unknown>): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    const traits: IdentifyTraitsProperties = this.getIdentifyTraits();
    // @ts-ignore
    this.segmentHelper.identify(this.user?.id, { ...traits, ...eventIdentifyProps });
  }

  private getDefaultProperties(): DefaultProperties {
    const properties: Record<string, unknown> = this.user ? { email: this.user.email } : {};

    return this.user instanceof AuthUser && this.user?.currentEstate
      ? {
          ...properties,
          estateName: this.user.currentEstate.contact.fullName,
          estateId: this.user.currentEstate.id,
          jurisdiction: this.user.currentEstate.jurisdiction.name,
          product: this.user.currentEstate.displayProductType,
          isDemo: this.user.currentEstate.isDemo,
          billingPlan: this.user.currentEstate?.billing?.planId ?? '',
          referralPartner: this.user.currentEstate?.company?.name ?? ''
        }
      : properties;
  }

  reset(): void {
    this.setUser(null);
    this.segmentHelper.reset();
  }

  private getIdentifyTraits(): IdentifyTraitsProperties {
    const identifyTraits: Record<string, unknown> = this.user
      ? {
          email: this.user.email,
          name: this.user.contact.fullName,
          createdAt: this.user.createdAt,
          language: Locale.getByKey(this.user.locale)?.value ?? '',
          daysSinceAccountCreated: this.getDaysSinceAccountCreation(this.user)
        }
      : {};
    return {
      href: window.location.href,
      ...identifyTraits,
      ...this.getDefaultProperties()
    };
  }

  private getTrackProperties(): TrackProperties {
    return {
      search: window.location.search,
      groupId: this.groupId || '',
      href: window.location.href,
      ...this.getDefaultProperties()
    };
  }

  private getPageProperties(path: string, referrer: string): PageProperties {
    return {
      path,
      search: window.location.search,
      url: path,
      title: document.title,
      referrer,
      ...this.getUserProperties(),
      ...this.getDefaultProperties()
    };
  }

  private getUserProperties(): UserProperties {
    return this.user
      ? {
          userId: this.user.id,
          userName: this.user.name,
          userRole: this.user.estateAccount?.roleName ?? this.user.roleName
        }
      : {};
  }

  private getDaysSinceAccountCreation(user: User): number {
    const dayInMilliSeconds: number = 1000 * 60 * 60 * 24;
    const timeDifference: number = new Date().getTime() - new Date(user.createdAt).getTime();

    return Math.round(timeDifference / dayInMilliSeconds);
  }
}

export default new SegmentService();
